<script setup>
import { computed, onMounted } from 'vue';
import HighlightsSection from '@/components/configuratorSections/HighlightsSection';
import PowertrainSection from '@/components/configuratorSections/PowertrainSection';
import CabColorSection from '@/components/configuratorSections/CabColorSection';
import StylingAndComfortSectionV2 from '@/components/configuratorSections/StylingAndComfortSectionV2';
import ChassisSectionV2 from '@/components/configuratorSections/ChassisSectionV2';
import FuelEfficiencySection from '@/components/configuratorSections/FuelEfficiencySection';
import ConnectedServicesSection from '@/components/configuratorSections/ConnectedServicesSection';
import SafetySectionGroup from '@/components/configuratorSections/SafetySectionGroup';
import RepairMaintenanceSection from '@/components/configuratorSections/RepairMaintenanceSection';
import { useServiceSavingsTexts } from '@/composables/serviceSavings';

import { useMainStore } from '@/store/index.js';

const store = useMainStore();
const { serviceSavingsTexts } = useServiceSavingsTexts();

const staticImagesBaseUrl = process.env.VUE_APP_DS_STATIC_IMAGES_BASE_URL;

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  offerTemplate: {
    type: String,
    default: 'PL_STANDARDA4X2NA_DW5',
    required: true,
  },
  specType: {
    type: String,
    default: '',
    required: true,
  },
  isFrameAgreement: {
    type: Boolean,
    default: false,
    required: true,
  },
});

const emit = defineEmits(['select']);

onMounted(() => {
  //set custom sections
  store.sections = [
      { id: 'highlights', name: 'highlightsHeaderTitle' },
      { id: 'powertrain', name: 'drivelineHeaderTitle' },
      { id: 'cab-color', name: 'cabColourHeaderTitle' },
      { id: 'styling-comfort', name: 'stylingComfortHeaderTitle' },
      { id: 'chassis', name: 'chassisHeaderTitle' },
      ...(options.value.safety ? [{ id: 'safety', name: 'safetyHeaderTitle' }] : []),
      ...(options.value.fuelEfficiency ? [{ id: 'fuel-efficiency', name: 'fuelEfficiencyHeaderTitle' }] : []),
      { id: 'connectedServices', name: 'connectedServicesHeaderTitle' },
      { id: 'repairMaintenance', name: 'repairMaintenanceHeaderTitle' },
    ];
});

const options = computed(() => {
  return store.options;
});

const images = computed(() => {
  return store.images;
});

const interiorImages = computed(() => {
  return { interior: images.value.interior, dashboard: images.value.dashboard };
});

const isBev = computed(() => {
  return props.offerTemplate.endsWith('_BEV');
});

const isDW5 = computed(() => {
  return props.offerTemplate.endsWith('_DW5');
});

const imageChassisSection = computed(() => {
  return (store.sectionMetadata?.chassis?.descriptionImage) ? `${staticImagesBaseUrl}/${store.sectionMetadata?.chassis?.descriptionImage}` : null;
});

const chassisHighlights = computed(() => {
  return store.misc?.chassisHighlights;
});
</script>
<template>
  <HighlightsSection
    v-if="options.highlights"
    :items="options.highlights"
  />
  <PowertrainSection
    :items="options.driveline"
    :options-container-style="{ 'justify-content': 'center' }"
    @select="emit('select', $event, 'driveline')"
  />
  <CabColorSection
    :items="options.cabColour"
    :front-image="images?.front"
    @select="emit('select', $event, 'cabColour')"
  />
  <StylingAndComfortSectionV2
    :items="options.stylingComfort"
    :images="interiorImages"
    :offer-template="offerTemplate"
    :spec-type="specType"
    :is-frame-agreement="isFrameAgreement"
    :is-d-w5="isDW5"
    @select="emit('select', $event, 'stylingComfort')"
  />
  <ChassisSectionV2
    :items="options.chassis"
    :images="{ side: imageChassisSection, isSide: images?.side }"
    :offer-template="offerTemplate"
    :spec-type="specType"
    :is-frame-agreement="isFrameAgreement"
    :is-bev="isBev"
    :is-d-w5="isDW5"
    :chassis-description-key="chassisHighlights"
    class="chasis-section"
    @select="emit('select', $event, 'chassis')"
  />
  <SafetySectionGroup
    :items="options.safety"
    @select="emit('select', $event, 'safety')"
  />
  <FuelEfficiencySection
    :items="options.fuelEfficiency"
    :images="{ view: images?.view }"
    @select="emit('select', $event, 'fuelEfficiency')"
  />
  <ConnectedServicesSection
    :items="options.connectedServices"
    :service-savings-texts="{ ...serviceSavingsTexts?.connectedServices, ...serviceSavingsTexts?.driverServices }"
    @select="emit('select', $event, 'connectedServices')"
  />
  <RepairMaintenanceSection
    :items="options.repairMaintenance"
    :service-savings-texts="serviceSavingsTexts?.repairMaintenance"
    @select="emit('select', $event, 'repairMaintenance')"
  />
</template>


<style scoped>
.chasis-section >>> .configurator-section__image > img {
  max-width: 70%;
  height: fit-content;
}

@media only screen and (max-width: 1294px) {
  .chasis-section >>> .configurator-section__image > img {
      max-width: 80%;
    }
}

@media only screen and (max-width: 1000px) {
  .chasis-section >>> .configurator-section__image > img {
      max-width: 90%;
    }
}

@media only screen and (max-width: 600px) {
  .chasis-section >>> .configurator-section__image > img {
    max-width: 120%;
  }
}
</style>
